/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import {
  Button,
  Flex
} from '@chakra-ui/react'
import LicensingFieldsForHookForm from '@/components/forms/predefined/LicensingFieldsForHookForm'
import AICompanyModels from '@/components/forms/predefined/AICompanyModels'
import InputCheckboxes from '@/components/forms/InputCheckboxes'
import Styles from './styles.module.sass'
import RecognizerBenchmarks from './components/RecognizerBenchmarks'
import FormWrapper from '../../FormWrapper'
import CircleLoader from '@/components/loaders/CircleLoader'
import { AI_LICENSING_PREFERENCES, LICENSE_GEOGRAPHY, SELECT_AI_COMPANIES } from '@/configs/strings/sams'
import { useBuildLicenseTerms } from './useBuildLicenseTerms'
import CheckboxesWrapper from './components/CheckboxesWrapper'

const BuildLicenseTerms = ({
  assetData,
  className = '',
  defaultValues = {},
  onClose,
  onSubmit,
  onUnlist
}) => {
  const {
    alert,
    currentOnSubmit,
    currentOnUnlist,
    errors,
    geographiesValue,
    handleSubmit,
    hasAlert,
    hasProvidedDefaults,
    isLoading,
    isSubmitting,
    llmCompanies,
    llmCompanyTagsValue,
    llmGeographies,
    llmTagsValue,
    onGeographiesChange,
    onLlmCompanyTagsChange,
    onLlmTagsChange,
    onSelectAllCompanyTags,
    onSelectAllGeographies,
    onSelectAllLlmTags,
    register,
    selectAllGeographiesState,
    selectAllLlmCompanyTagsState,
    selectAllLlmTagsState,
    setValue,
    watch
  } = useBuildLicenseTerms({ defaultValues, onUnlist, onSubmit })
  const flexDirection = {
    base: 'column',
    lg: 'row'
  }
  const checkboxClass = `${Styles.inputStyle} ${Styles.baseInputHeight}`

  if (isLoading) return <CircleLoader />

  return (
    <FormWrapper
      hasAlert={hasAlert}
      alertMessage={alert?.code}
    >
      { llmCompanies && llmGeographies &&
        <form
          onSubmit={handleSubmit(currentOnSubmit)}
          className={`flex-col space-y-4 ${className}`}
        >
          <h2 className="text-xl font-semibold">
            { AI_LICENSING_PREFERENCES }
          </h2>
          <div className="border p-4 bg-white">
            <Flex
              flexDirection={flexDirection}
              gap={{ base: '20px', md: '' }}
              className="gap-6"
            >
              <div className="flex-1">
                <LicensingFieldsForHookForm
                  errors={errors}
                  register={register}
                  numberLicensesKey="numberLicenses"
                />
              </div>
              <div className="flex-1">
                <CheckboxesWrapper
                  label="Recognizer AI to Test"
                  selectAllState={selectAllLlmTagsState}
                  onChangeSelectAll={onSelectAllLlmTags}
                >
                  <AICompanyModels
                    checkboxGroupsClassName={checkboxClass}
                    defaultValue={llmTagsValue}
                    errorMessage={errors.llmTagIds?.message}
                    key={llmTagsValue.join(',')}
                    models={llmCompanies?.data}
                    onChange={onLlmTagsChange}
                  />
                </CheckboxesWrapper>
              </div>
            </Flex>
            <Flex
              flexDirection={flexDirection}
              className="gap-6"
            >
              <div className="flex-1">
                <CheckboxesWrapper
                  label={LICENSE_GEOGRAPHY}
                  selectAllState={selectAllGeographiesState}
                  onChangeSelectAll={onSelectAllGeographies}
                >
                  <InputCheckboxes
                    key={geographiesValue.join(',')}
                    checkboxGroupsClassName={checkboxClass}
                    errorMessage={errors?.llmGeographiesIds?.message}
                    labelKey="name"
                    onChange={onGeographiesChange}
                    options={llmGeographies}
                    valueKey="id"
                    defaultValue={geographiesValue}
                  />
                </CheckboxesWrapper>
              </div>
              <div className="flex-1">
                <CheckboxesWrapper
                  label={SELECT_AI_COMPANIES}
                  selectAllState={selectAllLlmCompanyTagsState}
                  onChangeSelectAll={onSelectAllCompanyTags}
                >
                  <AICompanyModels
                    checkboxGroupsClassName={checkboxClass}
                    defaultValue={llmCompanyTagsValue}
                    errorMessage={errors.llmCompanyTagIds?.message}
                    key={llmCompanyTagsValue.join(',')}
                    models={llmCompanies?.data}
                    onChange={onLlmCompanyTagsChange}
                  />
                </CheckboxesWrapper>
              </div>
              <div className="flex-1">
                <RecognizerBenchmarks
                  value={watch('benchmarkText')}
                  onChange={(value) => setValue('benchmarkText', value)}
                  errorMessage={errors.benchmarkText?.message}
                />
              </div>
            </Flex>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button
              type="submit"
              isLoading={isSubmitting}
              loadingText="Saving..."
            >
              { hasProvidedDefaults
                ? 'Update'
                : 'List'
              }
            </Button>
            {
              onClose && assetData.listed && (
                <Button
                  type="button"
                  bg="red.500"
                  color="white"
                  _hover={{ bg: 'red.600' }}
                  onClick={currentOnUnlist}
                  isDisabled={isSubmitting}
                >
                  Unlist
                </Button>
              )
            }
          </div>
        </form>
      }
    </FormWrapper>
  )
}

export default BuildLicenseTerms
